import { useState } from 'react';
import type { PublicRegistrationMember } from '../../types/public-registration';

const AGE_GROUPS = [
  { value: 'adult', label: 'Adult (18+)' },
  { value: 'teen', label: 'Teen (13–17)' },
  { value: 'youth', label: 'Youth (6–12)' },
  { value: 'child', label: 'Child (3–5)' },
  { value: 'infant', label: 'Infant/Toddler (2 and under)' },
] as const;

const RELATIONSHIPS = [
  'self',
  'spouse',
  'partner',
  'child',
  'parent',
  'sibling',
  'niece',
  'nephew',
  'cousin',
  'grandparent',
  'grandchild',
  'other',
] as const;

const DIETARY_OPTIONS = ['vegetarian', 'vegan', 'gluten-free', 'dairy-free', 'halal', 'kosher', 'pescatarian'];

const inputClasses =
  'w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm text-slate-700 placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-primary transition-all outline-none';
const labelClasses = 'block text-xs font-bold text-slate-600 mb-1';

interface Props {
  index: number;
  member: PublicRegistrationMember;
  onChange: (m: PublicRegistrationMember) => void;
  onRemove?: () => void;
  /** When true, the name is taken from the Group Leader fields and shown read-only (row 0). */
  nameReadOnly?: boolean;
}

export default function FamilyMemberRow({ index, member, onChange, onRemove, nameReadOnly }: Props) {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const update = (patch: Partial<PublicRegistrationMember>) => onChange({ ...member, ...patch });

  const toggleDietary = (value: string) => {
    const current = member.dietaryRestrictions ?? [];
    const next = current.includes(value)
      ? current.filter((d) => d !== value)
      : [...current, value];
    update({ dietaryRestrictions: next });
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-bold text-slate-700">
          {index === 0 ? 'Group Leader' : `Family Member ${index + 1}`}
        </p>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="text-xs font-medium text-error hover:underline"
            aria-label={`Remove family member ${index + 1}`}
          >
            Remove
          </button>
        )}
      </div>

      {nameReadOnly ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <span className={labelClasses}>Name</span>
            <p id={`fm-name-${index}`} className="rounded-lg bg-slate-50 border border-slate-200 p-2.5 text-sm text-slate-700">
              {member.firstName || member.lastName
                ? `${member.firstName} ${member.lastName}`.trim()
                : 'From Group Leader above'}
            </p>
          </div>
          <div>
            <label className={labelClasses} htmlFor={`fm-age-${index}`}>Age group</label>
            <select
              id={`fm-age-${index}`}
              value={member.ageGroup}
              onChange={(e) => update({ ageGroup: e.target.value as PublicRegistrationMember['ageGroup'] })}
              className={inputClasses}
            >
              {AGE_GROUPS.map((g) => (
                <option key={g.value} value={g.value}>{g.label}</option>
              ))}
            </select>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className={labelClasses} htmlFor={`fm-first-${index}`}>First name</label>
            <input
              id={`fm-first-${index}`}
              type="text"
              value={member.firstName}
              onChange={(e) => update({ firstName: e.target.value })}
              className={inputClasses}
              placeholder="First"
            />
          </div>
          <div>
            <label className={labelClasses} htmlFor={`fm-last-${index}`}>Last name</label>
            <input
              id={`fm-last-${index}`}
              type="text"
              value={member.lastName}
              onChange={(e) => update({ lastName: e.target.value })}
              className={inputClasses}
              placeholder="Last"
            />
          </div>
          <div>
            <label className={labelClasses} htmlFor={`fm-age-${index}`}>Age group</label>
            <select
              id={`fm-age-${index}`}
              value={member.ageGroup}
              onChange={(e) => update({ ageGroup: e.target.value as PublicRegistrationMember['ageGroup'] })}
              className={inputClasses}
            >
              {AGE_GROUPS.map((g) => (
                <option key={g.value} value={g.value}>{g.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClasses} htmlFor={`fm-rel-${index}`}>Relationship</label>
            <select
              id={`fm-rel-${index}`}
              value={member.relationship}
              onChange={(e) => update({ relationship: e.target.value })}
              className={inputClasses}
            >
              {RELATIONSHIPS.map((r) => (
                <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setShowAdvanced((s) => !s)}
        className="mt-3 text-xs font-medium text-primary hover:underline"
        aria-expanded={showAdvanced}
      >
        {showAdvanced ? 'Hide dietary & accessibility details' : 'Add dietary & accessibility details'}
      </button>

      {showAdvanced && (
        <div className="mt-3 space-y-3 border-t border-slate-100 pt-3">
          <div>
            <span className={labelClasses}>Dietary restrictions</span>
            <div className="flex flex-wrap gap-2">
              {DIETARY_OPTIONS.map((opt) => {
                const active = (member.dietaryRestrictions ?? []).includes(opt);
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => toggleDietary(opt)}
                    aria-pressed={active}
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                      active
                        ? 'bg-primary text-white border-primary'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-primary'
                    }`}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className={labelClasses} htmlFor={`fm-allergy-${index}`}>Food allergies (optional)</label>
            <input
              id={`fm-allergy-${index}`}
              type="text"
              maxLength={500}
              value={member.foodAllergies ?? ''}
              onChange={(e) => update({ foodAllergies: e.target.value })}
              className={inputClasses}
              placeholder="e.g. Peanuts — severe / EpiPen required"
            />
          </div>
          <div>
            <label className={labelClasses} htmlFor={`fm-access-${index}`}>Accessibility needs (optional)</label>
            <input
              id={`fm-access-${index}`}
              type="text"
              maxLength={500}
              value={member.accessibilityNeeds ?? ''}
              onChange={(e) => update({ accessibilityNeeds: e.target.value })}
              className={inputClasses}
              placeholder="e.g. Wheelchair access, ground-floor room"
            />
          </div>
        </div>
      )}
    </div>
  );
}
